import { baseUrl } from '@/shared/constants'

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
type QueryParams = Record<string, string | number | boolean>;

interface RequestOptions {
  headers?: HeadersInit;
  body?: unknown;
  token?: string;
  queryParams?: QueryParams;
}

function buildUrl(endpoint: string, queryParams?: QueryParams): string {
  const url = baseUrl + endpoint;
  if (!queryParams) return url;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParams)) {
    query.append(key, value.toString());
  }

  return `${url}?${query.toString()}`;
}

async function httpRequest<T>(method: HttpMethod, endpoint: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(endpoint, options.queryParams);
  const isFormData = options.body instanceof FormData;
  const body = isFormData ? options.body as FormData : JSON.stringify(options.body)

  const response = await fetch(url, {
    method,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.token && { Authorization: `Bearer ${options.token}` }),
      /* in local_setup mode ngrok sends ERR_NGROK_6024 as html upon get requests,
         this header prevents it
      */
      ...(import.meta.env.MODE === 'local_setup' && { 'ngrok-skip-browser-warning': 'true' }),
      ...(options.headers || {}),
    },
    body: ['get', 'delete'].includes(method) ? undefined : body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }

  return response.json();
}

export const http = {
  get: <T>(url: string, options?: RequestOptions) => httpRequest<T>('get', url, options),
  post: <T>(url: string, options?: RequestOptions) => httpRequest<T>('post', url, options),
  put: <T>(url: string, options?: RequestOptions) => httpRequest<T>('put', url, options),
  patch: <T>(url: string, options?: RequestOptions) => httpRequest<T>('patch', url, options),
  delete: <T>(url: string, options?: RequestOptions) => httpRequest<T>('delete', url, options),
}